import { useJobillaOrgId } from '@/hooks'
import { useAuth } from '@/lib'
import { CampaignModel, CampaignStatus, Dashboard, DashboardCampaignModel, logger } from '@/ui'
import { campaignsIndexQueryKey, clients, useCampaignsIndex } from '@jobilla/nexus-sdk'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, Outlet, useNavigate } from '@tanstack/react-router'
import { parseISO } from 'date-fns'
import { useEffect, useState } from 'react'
import { extractErrorDetails, getErrorMessage } from './-dashboard-error'

export const Route = createFileRoute(
	'/_auth/company/$companyId/',
)({
	component: RouteComponent,
})

function RouteComponent() {
	const jobillaOrgId = useJobillaOrgId()
	const { user } = useAuth()
	const { companyId } = Route.useParams()
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	const [statusFilter, setStatusFilter] = useState('all')
	const [archivingCampaignIds, setArchivingCampaignIds] = useState<string[]>([])
	const [unarchivingCampaignIds, setUnarchivingCampaignIds] = useState<string[]>([])
	const [copyingCampaignIds, setCopyingCampaignIds] = useState<string[]>([])
	const fetchArchived = statusFilter === 'archived'

	const campaignArchiveMutation = useMutation({
		mutationFn: (campaignId: string) => {
			return clients.campaignArchive(campaignId)
		},
		onMutate: (campaignId) => {
			setArchivingCampaignIds((prev) => [...prev, campaignId])
		},
		onSuccess: async (_, campaignId) => {
			await queryClient.invalidateQueries({
				queryKey: campaignsIndexQueryKey(),
			})
			setArchivingCampaignIds((prev) => prev.filter((id) => id !== campaignId))
		},
		onError: (error, campaignId) => {
			const errorDetails = extractErrorDetails(error)
			logger.error(error, {
				category: 'dashboard',
				tags: { feature: 'campaign_archive' },
				data: {
					campaignId,
					status: errorDetails.status,
					message: errorDetails.message,
					jobillaOrgId,
				},
			})
			setArchivingCampaignIds((prev) => prev.filter((id) => id !== campaignId))
		},
	})

	const campaignUnarchiveMutation = useMutation({
		mutationFn: (campaignId: string) => {
			return clients.campaignUnarchive(campaignId)
		},
		onMutate: (campaignId) => {
			setUnarchivingCampaignIds((prev) => [...prev, campaignId])
		},
		onSuccess: async (_, campaignId) => {
			await queryClient.invalidateQueries({
				queryKey: campaignsIndexQueryKey(),
			})
			setUnarchivingCampaignIds((prev) => prev.filter((id) => id !== campaignId))
		},
		onError: (error, campaignId) => {
			const errorDetails = extractErrorDetails(error)
			logger.error(error, {
				category: 'dashboard',
				tags: { feature: 'campaign_unarchive' },
				data: {
					campaignId,
					status: errorDetails.status,
					message: errorDetails.message,
					jobillaOrgId,
				},
			})
			setUnarchivingCampaignIds((prev) => prev.filter((id) => id !== campaignId))
		},
	})

	const campaignCopyMutation = useMutation({
		mutationFn: (campaignId: string) => {
			return clients.campaignCopy(campaignId, { title: undefined })
		},
		onMutate: (campaignId) => {
			setCopyingCampaignIds((prev) => [...prev, campaignId])
		},
		onSuccess: async (_, campaignId) => {
			await queryClient.invalidateQueries({
				queryKey: campaignsIndexQueryKey(),
			})
			setCopyingCampaignIds((prev) => prev.filter((id) => id !== campaignId))
		},
		onError: (error, campaignId) => {
			const errorDetails = extractErrorDetails(error)
			logger.error(error, {
				category: 'dashboard',
				tags: { feature: 'campaign_copy' },
				data: {
					campaignId,
					status: errorDetails.status,
					message: errorDetails.message,
					jobillaOrgId,
				},
			})
			setCopyingCampaignIds((prev) => prev.filter((id) => id !== campaignId))
		},
	})

	const campaignsIndex = useCampaignsIndex({
		sort: ['created_at'],
		'filter[is_archived]': fetchArchived.toString(),
		'filter[organisation_id]': jobillaOrgId,
	}, {
		refetchInterval: 10000,
		select: (data) => {
			return {
				...data,
				data: data.map((campaign) => {
					return {
						...campaign,
						status: campaign.status as CampaignModel['status'],
						startedAt: campaign.startDate ? parseISO(campaign.startDate) : null,
						endsAt: campaign.endDate ? parseISO(campaign.endDate) : null,
						isEvergreen: campaign.isEvergreen,
						budget: campaign.budget ?? null,
						budgetBreakdown: campaign.budgetBreakdown
							? {
								metaBudget: campaign.budgetBreakdown.metaBudget,
								serviceFee: campaign.budgetBreakdown.serviceFee,
							}
							: null,
						candidateCount: campaign.candidateCount ?? null,
						candidatesToday: campaign.candidatesToday ?? null,
						candidatesPerDay: campaign.candidatesPerDay ?? null,
						hireCount: campaign.hireCount ?? null,
						formClosedAt: campaign.form?.closedAt ?? null,
						branding: campaign.branding
							? { logoUrl: campaign.branding.logo?.url ?? null, background: campaign.branding.background }
							: null,
						accessors: campaign.accessors ?? [],
						image: campaign.status === 'draft' ? campaign.feed?.image?.url ?? '' : campaign.image ?? '',
						location: campaign.location ?? '',
						isArchived: campaign.isArchived,
						updatedAt: new Date(campaign.updatedAt),
					} as DashboardCampaignModel
				}),
			}
		},
	})

	// Log dashboard fetch errors for debugging
	useEffect(() => {
		if (campaignsIndex.isError && campaignsIndex.error) {
			const errorDetails = extractErrorDetails(campaignsIndex.error)
			logger.error(campaignsIndex.error, {
				category: 'dashboard',
				tags: { feature: 'campaigns_fetch' },
				data: {
					status: errorDetails.status,
					statusText: errorDetails.statusText,
					message: errorDetails.message,
					code: errorDetails.code,
					jobillaOrgId,
					organizationSlug: companyId,
					statusFilter,
					fetchArchived,
				},
			})
		}
	}, [
		campaignsIndex.isError,
		campaignsIndex.error,
		jobillaOrgId,
		companyId,
		statusFilter,
		fetchArchived,
	])

	return (
		<>
			<Dashboard
				view={{
					campaigns: campaignsIndex.isSuccess ? campaignsIndex.data.data : [],
					organizationName: jobillaOrgId ?? '',
					userFirstName: user?.firstName ?? 'there',
					archivingCampaignIds,
					unarchivingCampaignIds,
					copyingCampaignIds,
				}}
				events={{
					onNewCampaign: () => {
						logger.track('Dashboard: Clicked Start', {
							data: {
								organisation_id: jobillaOrgId,
							},
						})

						void navigate({
							to: '/company/$companyId/campaign/new',
							params: {
								companyId,
							},
						})
					},
					onStatusFilterChange: setStatusFilter,
					onArchiveCampaign: async (campaignId: string) => {
						campaignArchiveMutation.mutate(campaignId)
					},
					onUnarchiveCampaign: async (campaignId: string) => {
						campaignUnarchiveMutation.mutate(campaignId)
					},
					onCopyCampaign: async (campaignId: string) => {
						campaignCopyMutation.mutate(campaignId)
					},
				}}
				loading={{ campaigns: campaignsIndex.isLoading }}
				errors={{
					campaigns: campaignsIndex.isError ? getErrorMessage(campaignsIndex.error) : false,
				}}
				renderers={{
					campaignLink: (
						campaignId: string,
						status: CampaignStatus,
						isArchived: boolean,
						props,
						destination,
					) => {
						const handleCampaignCardClick = () => {
							logger.track('Dashboard: Clicked Campaign Card', {
								data: {
									campaign_status: status,
									campaign_id: campaignId,
									organisation_id: jobillaOrgId,
								},
							})
						}

						if (status !== 'draft') {
							return destination === 'candidates'
								? (
									<Link
										to="/company/$companyId/$campaignId/candidates"
										params={{
											companyId,
											campaignId,
										}}
										disabled={isArchived || status === 'publishing'}
										resetScroll={false}
										onClick={handleCampaignCardClick}
										preloadDelay={400}
										{...props}
									/>
								)
								: (
									<Link
										to="/company/$companyId/$campaignId"
										params={{
											companyId,
											campaignId,
										}}
										disabled={isArchived || status === 'publishing'}
										resetScroll={false}
										onClick={handleCampaignCardClick}
										preloadDelay={400}
										{...props}
									/>
								)
						}

						return (
							<Link
								to="/company/$companyId/campaign/$campaignId"
								params={{
									companyId,
									campaignId,
								}}
								disabled={isArchived}
								resetScroll={false}
								onClick={handleCampaignCardClick}
								preloadDelay={400}
								{...props}
							/>
						)
					},
				}}
			/>
			<Outlet />
		</>
	)
}
